import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api, API_ROUTES } from "../components/Api";
import Loading from "../components/Loading";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faImage } from "@fortawesome/free-solid-svg-icons";

function UnityCardList() {
    const navigate = useNavigate();
    const [unityCards, setUnityCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [licenses, setLicenses] = useState([]);
    const [collections, setCollections] = useState([]);
    const [extensions, setExtensions] = useState([]);
    const [sortField, setSortField] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");

    const [filters, setFilters] = useState({
        license_id: "",
        collection_id: "",
        extension_id: "",
        language_id: "",
        quantity: "",
        booster: "",
        search: "",
    });

    const [searchInput, setSearchInput] = useState("");

    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    // Chargement des filtres
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [licensesRes, collectionsRes, extensionsRes] = await Promise.all([
                    api.get(API_ROUTES.LICENSECARDS),
                    api.get(API_ROUTES.COLLECTIONS),
                    api.get(API_ROUTES.EXTENSIONS),
                ]);

                setLicenses(licensesRes.data || []);
                setCollections(collectionsRes.data || []);
                setExtensions(extensionsRes.data || []);
            } catch (err) {
                console.error("Erreur lors du chargement des filtres:", err);
            }
        };
        fetchFilters();
    }, []);

    // Chargement des cartes
    useEffect(() => {
        const fetchUnityCards = async () => {
            setLoading(true);
            try {
                const res = await api.get(API_ROUTES.UNITYCARDS, {
                    params: { page, ...filters },
                });

                // Tri des qualités par id pour chaque carte
                const cardsWithSortedQualities = (res.data.data || []).map(card => {
                    if (card.qualities) {
                        card.qualities.sort((a, b) => a.id - b.id);
                    }
                    return card;
                });

                setUnityCards(cardsWithSortedQualities);
                setTotalPages(res.data.last_page || 1);
            } catch (err) {
                console.error("Erreur lors du chargement des cartes:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUnityCards();
    }, [filters, page]);

    useEffect(() => {
        const fetchUnityCards = async () => {
            setLoading(true);
            try {
                const res = await api.get(API_ROUTES.UNITYCARDS, {
                    params: {
                        page,
                        ...filters,
                        order_by: sortField,
                        order_dir: sortDirection
                    },
                });

                const cardsWithSortedQualities = (res.data.data || []).map(card => {
                    if (card.qualities) {
                        card.qualities.sort((a, b) => a.id - b.id);
                    }
                    return card;
                });

                setUnityCards(cardsWithSortedQualities);
                setTotalPages(res.data.last_page || 1);
            } catch (err) {
                console.error("Erreur lors du chargement des cartes:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUnityCards();
    }, [filters, page, sortField, sortDirection]);

    if (loading) return <Loading message="Chargement des cartes..." />;

    // Filtrage dépendant : collections selon license, extensions selon collection
    const filteredCollections = filters.license_id
        ? collections.filter(c => c.license_card_id === parseInt(filters.license_id))
        : collections;

    const filteredExtensions = filters.collection_id
        ? extensions.filter(e => e.collection_id === parseInt(filters.collection_id))
        : extensions;

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    return (
        <div className="container mx-auto p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 md:grid-cols-4 gap-4 mb-4">
                <h1 className="text-2xl font-bold dark:text-white">Liste des Cartes Uniques</h1>
                <div></div>
                <div></div>
                <button
                    onClick={() => { navigate("/add-unity_card"); }}
                    className="bg-green-500 hover:bg-green-600 text-white dark:text-black px-4 rounded"
                >
                    Ajouter une carte / booster
                </button>
            </div>

            {/* Barre de recherche */}
            <div className="flex flex-col sm:flex-row mb-4">
                <input
                    type="text"
                    placeholder="Recherche par nom ou référence"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            setFilters({ ...filters, search: searchInput });
                            setPage(1);
                        }
                    }}
                    className="border p-2 rounded sm:rounded-r-none mb-2 sm:mb-0 flex-1 dark:bg-gray-700 bg-white"
                />
                <button
                    onClick={() => {
                        setFilters({ ...filters, search: searchInput });
                        setPage(1);
                    }}
                    className="bg-blue-500 text-white px-4 rounded sm:rounded-l-none border"
                >
                    Rechercher
                </button>
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                <select
                    value={filters.booster}
                    onChange={(e) => setFilters({ ...filters, booster: e.target.value })}
                    className="border p-2 dark:bg-gray-700 bg-white"
                >
                    <option value="">-- Tous --</option>
                    <option value="1">Booster</option>
                    <option value="0">Carte unique</option>
                </select>

                <select
                    value={filters.license_id}
                    onChange={(e) =>
                        setFilters({
                            ...filters,
                            license_id: e.target.value,
                            collection_id: "",
                            extension_id: "",
                        })
                    }
                    className="border p-2 dark:bg-gray-700 bg-white"
                >
                    <option value="">-- License --</option>
                    {licenses.map(l => (
                        <option key={l.id} value={l.id}>{l.license_card}</option>
                    ))}
                </select>

                {filters.license_id && (
                    <select
                        value={filters.collection_id}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                collection_id: e.target.value,
                                extension_id: "",
                            })
                        }
                        className="border p-2 dark:bg-gray-700 bg-white"
                    >
                        <option value="">-- Collection --</option>
                        {filteredCollections.map(c => (
                            <option key={c.id} value={c.id}>{c.collection_name}</option>
                        ))}
                    </select>
                )}

                {filters.collection_id && (
                    <select
                        value={filters.extension_id}
                        onChange={(e) => setFilters({ ...filters, extension_id: e.target.value })}
                        className="border p-2 dark:bg-gray-700 bg-white"
                    >
                        <option value="">-- Extension --</option>
                        {filteredExtensions.map(e => (
                            <option key={e.id} value={e.id}>{e.extension_name}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Tableau desktop */}
            <div className="hidden xl:block">
                <table className="min-w-full border">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-center">
                        <tr>
                            <th className="border px-2 py-1">Vue</th>

                            {/* Tri sur référence */}
                            <th
                                className="border px-2 py-1 cursor-pointer select-none"
                                onClick={() => handleSort("reference")}
                            >
                                Référence{" "}
                                {sortField === "reference" && (
                                    <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                                )}
                            </th>

                            <th className="border px-2 py-1">License</th>
                            <th className="border px-2 py-1">Extension</th>
                            <th className="border px-2 py-1">Image</th>
                            <th className="border px-2 py-1">Nom</th>
                            <th className="border px-2 py-1">Rareté</th>
                            <th className="border px-2 py-1">Quantité</th>
                            <th className="border px-2 py-1">Côtation</th>
                            <th className="border px-2 py-1">Langue</th>
                            <th className="border px-2 py-1">Liens</th>
                        </tr>
                    </thead>

                    <tbody>
                        {unityCards.map((u, index) => {
                            const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-50" : "dark:bg-gray-700 bg-gray-100";
                            return (
                                <tr key={u.id} className={`${bgColor}`}>
                                    <td className="border px-2 py-1 text-center">
                                        <a href={`/unity_card/${u.id}`} className="text-blue-500 underline">
                                            <FontAwesomeIcon icon={faEye} />
                                        </a>
                                    </td>
                                    <td className="border px-2 py-1 text-center">{u.reference}</td>
                                    <td className="border px-2 py-1 text-center">{u.license_card?.license_card}</td>
                                    <td className="border px-2 py-1 text-center">{u.extension?.extension_name || "---"}</td>
                                    <td className="border px-2 py-1 text-center">
                                        {u.picture ? (
                                            <img src={`${API_ROUTES.IMG}${u.picture}`} alt={u.name} style={{ maxWidth: "128px", maxHeight: "128px" }} className="m-auto" />
                                        ) : <FontAwesomeIcon icon={faImage} />}
                                    </td>
                                    <td className="border px-2 py-1 text-center">{u.name}</td>
                                    <td className="border px-2 py-1 text-center">{u.booster ? "Booster" : u.rarity?.rarity_name}</td>
                                    <td className="border px-2 py-1 text-center">
                                        {u.qualities && u.qualities.length > 0 ? (
                                            <ul className="space-y-1">
                                                {u.qualities.map(q => (
                                                    <li key={q.id}>
                                                        {`${q.quality_card_name} : ${q.pivot.quantity}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : "---"}
                                    </td>
                                    <td className="border px-2 py-1 text-center">
                                        {u.qualities && u.qualities.length > 0 ? (
                                            <ul className="space-y-1">
                                                {u.qualities.map(q => (
                                                    <li key={q.id}>
                                                        {`${((Number(u.sell) || 0) * ((-20 * q.id + 120) / 100)).toFixed(2)} €`}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : "---"}
                                    </td>
                                    <td className="border px-2 py-1 text-center">{u.language?.language_name}</td>
                                    <td className="border px-2 py-1 text-center">
                                        {u.links && u.links.length > 0 && (
                                            <div className="space-y-2 mt-4">
                                                {u.links.map(link => (
                                                    <div>
                                                        <a
                                                            href={link.link_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="dark:text-blue-300 text-blue-500"
                                                        >
                                                            {link.type_link?.type_link_name || "Lien"}
                                                        </a>
                                                        <br />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Cartes mobiles */}
            <div className="block xl:hidden space-y-4">
                {unityCards.map(u => (
                    <div key={u.id} className="border rounded p-2 shadow-sm grid grid-cols-3 gap-2 items-start">
                        <div className="flex flex-col space-y-1">
                            <span><strong>Nom:</strong> {u.name}</span>
                            <span><strong>Réf:</strong> {u.reference}</span>
                            <span><strong>License:</strong> {u.license_card?.license_card}</span>
                            <span><strong>Collection:</strong> {u.collection?.collection_name}</span>
                            <span><strong>Extension:</strong> {u.extension?.extension_name || "-"}</span>
                        </div>

                        <div className="flex flex-col space-y-1">
                            <span><strong>Booster:</strong> {u.booster ? "Oui" : "Non"}</span>
                            <span><strong>Langue:</strong> {u.language?.language_name}</span>
                            <span><strong>Rareté:</strong> {u.rarity?.rarity_name}</span>
                            <span><strong>Quantité:</strong></span>
                            {u.qualities && u.qualities.length > 0 ? (
                                u.qualities.map(q => (
                                    <div key={q.id}>
                                        {q.quality_card_name || "---"} : {q.pivot.quantity}
                                    </div>
                                ))
                            ) : "-"}
                        </div>

                        <div className="flex flex-col items-end space-y-2">
                            {u.picture ? (
                                <img src={`${API_ROUTES.IMG}${u.picture}`} alt={u.name} className="w-24 h-24 object-contain" />
                            ) : (
                                <FontAwesomeIcon icon={faImage} className="w-6 h-6" />
                            )}
                            <a href={`/unity_card/${u.id}`} className="dark:text-blue-300 text-blue-500 underline flex items-center space-x-1">
                                <FontAwesomeIcon icon={faEye} />
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center space-x-2 mt-4 flex-wrap">
                <button disabled={page <= 1} onClick={() => setPage(1)} className="border px-2 py-1 rounded dark:bg-gray-600 bg-gray-300">&lt;&lt;&lt;</button>
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border px-2 py-1 rounded dark:bg-gray-500 bg-gray-200">&lt;</button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2))
                    .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        return (
                            <React.Fragment key={p}>
                                {prev && p - prev > 1 && <span className="px-1">...</span>}
                                <button
                                    onClick={() => setPage(p)}
                                    className={`border px-2 py-1 rounded ${p === page ? "bg-blue-500 text-white dark:text-black" : "dark:bg-gray-400 bg-gray-100"}`}
                                >
                                    {p}
                                </button>
                            </React.Fragment>
                        );
                    })}

                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="border px-2 py-1 rounded dark:bg-gray-500 bg-gray-200">&gt;</button>
                <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="border px-2 py-1 rounded dark:bg-gray-600 bg-gray-300">&gt;&gt;&gt;</button>
            </div>
        </div >
    );
}

export default UnityCardList;
